<template>
  <div class="pagination-container" v-if="totalPages > 1">
    <div class="pagination-info">Exibindo de {{ from }} a {{ to }},<br>total de {{ total }}.</div>

    <div class="pagination-controls">
      <button class="pagination-btn acao-secundaria" :disabled="currentPage === 1" @click="changePage(1)" title="Primeira página">
        <span>«</span>
      </button>

      <button class="pagination-btn acao-secundaria" :disabled="currentPage === 1" @click="changePage(currentPage - 1)" title="Página anterior">
        <span>‹</span>
      </button>

      <button
        v-for="page in visiblePages"
        :key="page"
        class="pagination-btn acao-secundaria"
        :class="{ active: page === currentPage, ellipsis: page === '...' }"
        @click="page !== '...' ? changePage(page) : null"
        :disabled="page === '...'"
      >
        {{ page }}
      </button>

      <button class="pagination-btn acao-secundaria" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)" title="Próxima página">
        <span>›</span>
      </button>

      <button class="pagination-btn acao-secundaria" :disabled="currentPage === totalPages" @click="changePage(totalPages)" title="Última página">
        <span>»</span>
      </button>
    </div>

    <div class="per-page-selector">
      <label>Por página:</label>
      <select :value="perPage" @change="changePerPage($event.target.value)">
        <option v-for="option in perPageOptions" :key="option" :value="option">
          {{ option }}
        </option>
      </select>
    </div>
  </div>
</template>

<script>
export default {
  name: "PaginationComponent",

  props: {
    currentPage: {
      type: Number,
      required: true,
      default: 1,
    },
    totalPages: {
      type: Number,
      required: true,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 30,
    },
    total: {
      type: Number,
      required: true,
      default: 0,
    },
    from: {
      type: Number,
      default: 0,
    },
    to: {
      type: Number,
      default: 0,
    },
    perPageOptions: {
      type: Array,
      default: () => [10, 20, 30, 50, 100],
    },
  },

  computed: {
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2; // Number of pages to show before and after current page

      if (total <= 7) {
        // Show all pages if total is small
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);

        // Calculate start and end of middle section
        let start = Math.max(2, current - delta);
        let end = Math.min(total - 1, current + delta);

        // Add ellipsis after first page if needed
        if (start > 2) {
          pages.push("...");
        }

        // Add middle pages
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        // Add ellipsis before last page if needed
        if (end < total - 1) {
          pages.push("...");
        }

        // Always show last page
        pages.push(total);
      }

      return pages;
    },
  },

  methods: {
    changePage(page) {
      if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
        this.$emit("page-changed", page);
      }
    },

    changePerPage(value) {
      this.$emit("per-page-changed", parseInt(value));
    },
  },
};
</script>

<style scoped>
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 15px;
  gap: 20px;
  flex-wrap: wrap;
}

.pagination-info {
  color: var(--cor-texto, #333);
  font-size: 14px;
  white-space: nowrap;
  text-align: end
}

.pagination-controls {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  justify-content: center;
}

.pagination-btn {
  min-width: 36px;
  height: 36px;
  padding: 8px 12px;
  border: 1px solid var(--cor-separador, #ddd);
  background-color: white;
  color: var(--cor-texto, #333);
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover:not(:disabled):not(.ellipsis) {
  background-color: var(--cor-primaria-clara, #e3f2fd);
  border-color: var(--cor-primaria-media, #2196f3);
  color: var(--cor-primaria-forte, #1976d2);
}

.pagination-btn.active {
  background-color: var(--cor-primaria-media, #2196f3);
  color: black;
  border-color: var(--cor-primaria-media, #1976d2);
  font-weight: bold;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-btn.ellipsis {
  cursor: default;
  border: none;
  background: transparent;
}

.pagination-btn.ellipsis:hover {
  background: transparent;
  border: none;
}

.per-page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.per-page-selector label {
  font-size: 14px;
  color: var(--cor-texto, #333);
}

.per-page-selector select {
  padding: 6px 30px 6px 10px;
  border: 1px solid var(--cor-separador, #ddd);
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  font-size: 14px;
  color: var(--cor-texto, #333);
}

.per-page-selector select:focus {
  outline: none;
  border-color: var(--cor-primaria-media, #2196f3);
}

/* Responsive design */
@media (max-width: 768px) {
  .pagination-container {
    flex-direction: column;
    gap: 15px;
  }

  .pagination-info,
  .per-page-selector {
    width: 100%;
    justify-content: center;
  }

  .pagination-controls {
    width: 100%;
  }
}
</style>
